import React, { useState, useEffect, createContext, useContext } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from './firebase';
import { rolesFor } from './utils/roles';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Privacy from './pages/Privacy';
import RefundPolicy from './pages/RefundPolicy';

// Global context for auth + team role
export const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [teams, setTeams] = useState([]);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('landing');

  // Auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        await loadUserData(currentUser.uid);
      } else {
        setUserProfile(null);
        setTeams([]);
        setCurrentTeam(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Load user profile + teams
  const loadUserData = async (uid) => {
    try {
      // User profile
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        setUserProfile({ id: userDoc.id, ...userDoc.data() });
      }

      // 팀 목록 — 운영진이 memberIds 에 없는 데이터도 있으므로
      // memberIds / adminIds / coachIds 세 쿼리의 합집합으로 만든다.
      // 한 쿼리가 거부되어도 나머지는 살리기 위해 각각 따로 처리한다.
      const teamsOf = async (field) => {
        try {
          const snap = await getDocs(
            query(collection(db, 'teams'), where(field, 'array-contains', uid))
          );
          return snap.docs;
        } catch (err) {
          console.error(`teams by ${field} failed:`, err);
          return [];
        }
      };
      const results = await Promise.all(
        ['memberIds', 'adminIds', 'coachIds'].map(teamsOf)
      );
      const byId = new Map();
      results.forEach(docs => docs.forEach(d => byId.set(d.id, { id: d.id, ...d.data() })));
      const teamList = Array.from(byId.values());
      setTeams(teamList);

      // Restore last team or default
      const savedTeamId = localStorage.getItem('ionice_lastTeamId');
      if (savedTeamId && teamList.find(t => t.id === savedTeamId)) {
        setCurrentTeam(teamList.find(t => t.id === savedTeamId));
      } else if (teamList.length > 0) {
        setCurrentTeam(teamList[0]);
      }

      // 모드 기능을 없앴으므로 남은 값은 정리한다.
      localStorage.removeItem('ionice_lastMode');
    } catch (err) {
      console.error('Failed to load user data:', err);
    }
  };

  // 현재 팀 문서 구독 — 다른 기기에서 역할(adminIds/coachIds)이 바뀌면 바로 반영된다.
  const currentTeamId = currentTeam?.id;
  useEffect(() => {
    if (!user || !currentTeamId) return;
    const unsub = onSnapshot(
      doc(db, 'teams', currentTeamId),
      (snap) => {
        if (!snap.exists()) return;
        const fresh = { id: snap.id, ...snap.data() };
        setCurrentTeam(fresh);
        setTeams(prev => prev.map(t => (t.id === fresh.id ? fresh : t)));
      },
      (err) => console.error('team subscribe failed:', err)
    );
    return () => unsub();
  }, [user, currentTeamId]);

  // Routing
  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/privacy') setCurrentPage('privacy');
    else if (path === '/refund') setCurrentPage('refund');
    else if (path === '/login') setCurrentPage('login');
    else if (path.startsWith('/dashboard')) setCurrentPage('dashboard');
    else setCurrentPage('landing');

    const handlePopState = () => {
      const p = window.location.pathname;
      if (p === '/privacy') setCurrentPage('privacy');
      else if (p === '/refund') setCurrentPage('refund');
      else if (p === '/login') setCurrentPage('login');
      else if (p.startsWith('/dashboard')) setCurrentPage('dashboard');
      else setCurrentPage('landing');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (page) => {
    const path = page === 'landing' ? '/' : `/${page}`;
    window.history.pushState({}, '', path);
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const switchTeam = (team) => {
    setCurrentTeam(team);
    localStorage.setItem('ionice_lastTeamId', team.id);
  };

  // 권한 판정 — 팀 문서(ownerId/adminIds/coachIds/memberIds)만 본다.
  // teams/{t}/members/{uid}.role 은 규칙도 iOS 도 보지 않으므로 쓰지 않는다.
  const roles = rolesFor(currentTeam, user?.uid);

  const contextValue = {
    user, userProfile, teams, currentTeam,
    navigate, switchTeam,
    loading, loadUserData,
    ...roles,          // isOwner, isAdmin, isCoach, isStaff, isMember, roleKey
    roles,             // requireRole(roles, 'isAdmin') 용
    currentRole: roles.roleKey,   // 표시용
  };

  if (loading && currentPage !== 'privacy' && currentPage !== 'refund' && currentPage !== 'landing') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
        minHeight: '100vh', backgroundColor: '#0A1220' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#5BC4D8',
            fontFamily: "'Rajdhani', sans-serif", letterSpacing: '3px' }}>IonIce</div>
          <div style={{ color: '#556677', marginTop: '12px', fontSize: '14px' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <AppContext.Provider value={contextValue}>
      {currentPage === 'privacy' && <Privacy navigate={navigate} />}
      {currentPage === 'refund' && <RefundPolicy navigate={navigate} />}
      {currentPage === 'landing' && <Landing navigate={navigate} user={user} />}
      {currentPage === 'login' && (
        user ? (() => { navigate('dashboard'); return null; })()
          : <Login navigate={navigate} />
      )}
      {currentPage === 'dashboard' && (
        user ? <Dashboard />
          : (() => { navigate('login'); return null; })()
      )}
    </AppContext.Provider>
  );
}

export default App;
